import type { InfiniteData } from "@tanstack/vue-query";
import { computed, ref, watch } from "vue";
import { useInfiniteQuery } from "@tanstack/vue-query";
import type { Reactive } from "vue";
import { getRecordList, type RecordResponse } from "@/api/record";

interface RecordFilters {
  agent_id: string;
  size: number;
  // backend supports filtering by mode ("", "auto" or "manual")
  mode?: string;
}

type RecordQueryKey = ["records", Omit<RecordFilters, "page">];

const fetchRecords = async ({
  queryKey,
  pageParam = 1,
}: {
  queryKey: RecordQueryKey;
  pageParam?: number;
}): Promise<RecordResponse> => {
  const [, filters] = queryKey;

  return getRecordList({
    ...filters,
    page: pageParam,
  });
};

export function useRecordsInfiniteQuery(
  filters: Reactive<RecordFilters>,
) {
  const queryKey = ref<RecordQueryKey>(["records", filters]);

  watch(
    filters,
    (newFilters) => {
      queryKey.value = ["records", newFilters];
    },
    { deep: true, immediate: true },
  );

  const queryResult = useInfiniteQuery<
    RecordResponse,
    Error,
    InfiniteData<RecordResponse>,
    RecordQueryKey,
    number
  >({
    queryKey: queryKey,
    queryFn: fetchRecords,
    initialPageParam: 1,
    getNextPageParam: (
      lastPage: RecordResponse,
      allPages: RecordResponse[],
    ) => {
      const totalItems = lastPage.total_filter;
      const pageSize = filters.size || 20;
      const totalPages = Math.ceil(totalItems / pageSize);
      const currentPage = allPages.length;

      return currentPage < totalPages ? currentPage + 1 : undefined;
    },
    staleTime: 5 * 60 * 1000,
  });

  const records = computed(() => {
    const pages = queryResult.data.value?.pages;
    if (!pages) return [];
    return pages.flatMap((page: RecordResponse) => page.data || []);
  });

  const totalFiltered = computed(() => {
    const pages = queryResult.data.value?.pages;
    return pages?.[0]?.total_filter || 0;
  });

  return {
    ...queryResult,
    records,
    totalFiltered,
  };
}
